import { isAnalyzerResult } from './report.js';
const PERFORMANCE_CADENCE = {
    quality: {},
    balanced: {
        'video-artifacts': 2,
        'tracking-markers': 2,
    },
    realtime: {
        'lighting-setup': 2,
        'video-artifacts': 4,
        'edge-confidence': 2,
        'tracking-markers': 4,
    },
};
const roundMs = (value) => Number(value.toFixed(2));
export class LimonadeEyeEngine {
    analyzers = new Map();
    lastAnalyzerResults = new Map();
    frameNumber = 0;
    constructor(analyzers = []) {
        for (const analyzer of analyzers)
            this.registerAnalyzer(analyzer);
    }
    registerAnalyzer(analyzer) {
        if (!analyzer.id.trim())
            throw new Error('Analyzer id must not be empty.');
        if (this.analyzers.has(analyzer.id)) {
            throw new Error(`Analyzer "${analyzer.id}" is already registered.`);
        }
        this.analyzers.set(analyzer.id, analyzer);
        return this;
    }
    unregisterAnalyzer(id) {
        this.lastAnalyzerResults.delete(id);
        return this.analyzers.delete(id);
    }
    reset() {
        this.frameNumber = 0;
        this.lastAnalyzerResults.clear();
        for (const analyzer of this.analyzers.values()) {
            analyzer.reset?.();
        }
    }
    listAnalyzers() {
        return [...this.analyzers.values()].map(({ id, label }) => ({ id, label }));
    }
    async analyzeFrame(frame, options = {}) {
        if (!frame || frame.width <= 0 || frame.height <= 0 || !frame.data) {
            throw new Error('LimonadeEyeEngine.analyzeFrame expected ImageData with positive width, height, and pixel data.');
        }
        const startedAt = performance.now();
        const context = {
            frameNumber: ++this.frameNumber,
            timestampMs: options.timestampMs ?? startedAt,
            width: frame.width,
            height: frame.height,
        };
        const results = [];
        const engineWarnings = [];
        const analyzerTimings = {};
        const analyzerList = [...this.analyzers.values()];
        const enabled = options.enabledAnalyzerIds ? new Set(options.enabledAnalyzerIds) : undefined;
        const disabled = new Set(options.disabledAnalyzerIds ?? []);
        const modeCadence = PERFORMANCE_CADENCE[options.performanceMode ?? 'quality'];
        const cadence = { ...modeCadence, ...options.analyzerCadence };
        const reuseSkippedResults = options.reuseSkippedResults ?? true;
        for (const analyzer of analyzerList) {
            if (enabled && !enabled.has(analyzer.id))
                continue;
            if (disabled.has(analyzer.id))
                continue;
            const everyNFrames = Math.max(1, Math.floor(cadence[analyzer.id] ?? 1));
            const cached = this.lastAnalyzerResults.get(analyzer.id);
            const shouldRun = everyNFrames <= 1 || (context.frameNumber - 1) % everyNFrames === 0 || !cached;
            if (!shouldRun && reuseSkippedResults && cached) {
                const reused = this.reuseResult(cached, context);
                results.push(reused);
                analyzerTimings[analyzer.id] = 0;
                continue;
            }
            const analyzerStartedAt = performance.now();
            const analyzerResults = new Map(results.map((result) => [result.module_id, result]));
            const executionContext = { ...context, analyzerResults };
            try {
                const result = await Promise.resolve().then(() => analyzer.analyze(frame, executionContext));
                if (!isAnalyzerResult(result)) {
                    engineWarnings.push({
                        code: 'invalid_analyzer_result',
                        severity: 'error',
                        module_id: analyzer.id,
                        frame_number: context.frameNumber,
                        message: `Analyzer "${analyzer.id}" returned an invalid AnalyzerResult shape.`,
                    });
                    continue;
                }
                if (result.module_id !== analyzer.id) {
                    engineWarnings.push({
                        code: 'analyzer_module_id_mismatch',
                        severity: 'warning',
                        module_id: analyzer.id,
                        frame_number: context.frameNumber,
                        message: `Analyzer "${analyzer.id}" returned module_id "${result.module_id}".`,
                    });
                }
                results.push(result);
                this.lastAnalyzerResults.set(analyzer.id, result);
            }
            catch (reason) {
                engineWarnings.push({
                    code: 'analyzer_failed',
                    severity: 'error',
                    module_id: analyzer.id,
                    frame_number: context.frameNumber,
                    message: `Analyzer "${analyzer.id}" failed: ${reason instanceof Error ? reason.message : String(reason)}`,
                });
            }
            analyzerTimings[analyzer.id] = roundMs(performance.now() - analyzerStartedAt);
        }
        return {
            frame_number: context.frameNumber,
            timestamp_ms: context.timestampMs,
            width: context.width,
            height: context.height,
            processing_duration_ms: roundMs(performance.now() - startedAt),
            analyzer_timings_ms: analyzerTimings,
            analyzers: results,
            warnings: [...results.flatMap((result) => result.warnings), ...engineWarnings],
            overlays: results.flatMap((result) => result.overlays),
        };
    }
    reuseResult(result, context) {
        return {
            ...result,
            frame_number: context.frameNumber,
            timestamp_ms: context.timestampMs,
            warnings: result.warnings.map((warning) => ({
                ...warning,
                frame_number: context.frameNumber,
            })),
        };
    }
}
//# sourceMappingURL=LimonadeEyeEngine.js.map